/**
 * Keep in sync with supabase/functions/_shared/types/session.ts (the Deno edge
 * runtime cannot import from src/).
 */
import { Session } from "@/hooks/useSessions"

import type { Json } from "../../database.types"

export interface KnownSessionContentFields {
  location_address?: string
}

export type UnknownContentFieldKeys = { [key: string]: Json | undefined }

export type SessionContentFields = KnownSessionContentFields & UnknownContentFieldKeys

const isPlainObject = (value: unknown): value is UnknownContentFieldKeys =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const parseSessionContentFields = (raw: unknown): SessionContentFields => {
  if (!isPlainObject(raw)) return {}

  const { location_address, ...rest } = raw
  const address = typeof location_address === "string" ? location_address.trim() : ""

  return address ? { ...rest, location_address: address } : rest
}

/**
 * Builds a `content_fields` value for writing. Returns null when there is
 * nothing to store, so we don't litter the column with empty objects.
 */
export const buildSessionContentFields = (
  fields: SessionContentFields,
): SessionContentFields | null => {
  const parsed = parseSessionContentFields(fields)
  return Object.keys(parsed).length > 0 ? parsed : null
}

/**
 * The address to show a driver for a session: the per-session override when ops
 * set one, otherwise the depot address. Returns "" when neither is available.
 */
export const resolveSessionAddress = (session: Session): string =>
  parseSessionContentFields(session?.content_fields).location_address ??
  [session?.depot?.address, session?.depot?.city].filter(Boolean).join(", ")
